/**
 * Byzantine Fault Tolerant Consensus for P2P Poker
 * 
 * Implements a practical Byzantine Fault Tolerance (pBFT) consensus algorithm
 * specifically adapted for poker gameplay. Ensures game state consistency
 * even with malicious or faulty peers.
 * 
 * Features:
 * - 3-phase consensus (prepare, commit, reply)
 * - Cryptographic message signatures
 * - Byzantine fault tolerance (supports up to 1/3 malicious nodes)
 * - State synchronization and conflict resolution
 * - Timeout handling and view changes
 */

interface ConsensusMessage {
  type: 'prepare' | 'commit' | 'reply';
  sequenceNumber: number;
  view: number;
  digest: string;
  payload: any;
  senderId: string;
  timestamp: number;
  signature: string;
}

interface ConsensusState {
  view: number;
  sequenceNumber: number;
  phase: 'prepare' | 'commit' | 'reply' | 'idle';
  primaryNode: string;
  lastCommittedSequence: number;
  messageLog: Map<string, ConsensusMessage>;
  prepareMessages: Map<number, ConsensusMessage[]>;
  commitMessages: Map<number, ConsensusMessage[]>;
  replyMessages: Map<number, ConsensusMessage[]>;
}

interface ByzantineNode {
  id: string;
  publicKey: string;
  isActive: boolean;
  lastHeartbeat: number;
  reputation: number; // Anti-cheat reputation score
}

/**
 * Byzantine Fault Tolerant Consensus Engine
 * Ensures consistent game state across all honest players
 */
export class ByzantineConsensus {
  private static instance: ByzantineConsensus;
  
  // Consensus state
  private state: ConsensusState;
  private nodes: Map<string, ByzantineNode> = new Map();
  private localNodeId: string = '';
  private privateKey: CryptoKey | null = null;
  
  // Consensus parameters
  private readonly CONSENSUS_TIMEOUT = 10000; // 10 seconds
  private readonly HEARTBEAT_INTERVAL = 5000; // 5 seconds
  private readonly MAX_BYZANTINE_NODES: number; // f = (n-1)/3
  private readonly MIN_CONSENSUS_NODES = 2; // Minimum nodes for consensus (relaxed for poker)
  
  // Message handlers
  private consensusReachedHandler?: (sequenceNumber: number, payload: any) => void;
  private viewChangeHandler?: (newView: number, newPrimary: string) => void;
  private byzantineDetectedHandler?: (suspiciousNode: string, evidence: any) => void;
  
  // Timers
  private consensusTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.state = {
      view: 0,
      sequenceNumber: 0,
      phase: 'idle',
      primaryNode: '',
      lastCommittedSequence: -1,
      messageLog: new Map(),
      prepareMessages: new Map(),
      commitMessages: new Map(),
      replyMessages: new Map()
    };
    
    this.MAX_BYZANTINE_NODES = 0; // Will be calculated based on network size
  }

  static getInstance(): ByzantineConsensus {
    if (!ByzantineConsensus.instance) {
      ByzantineConsensus.instance = new ByzantineConsensus();
    }
    return ByzantineConsensus.instance;
  }

  /**
   * Initialize consensus with network participants
   */
  async initialize(nodeId: string, participantIds: string[]): Promise<void> {
    console.log('🛡️ Initializing Byzantine consensus...');
    
    this.localNodeId = nodeId;
    
    // Generate cryptographic key pair for message signing
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'ECDSA',
        namedCurve: 'P-256'
      },
      true,
      ['sign', 'verify']
    );
    
    this.privateKey = keyPair.privateKey;
    
    // Initialize network nodes
    for (const participantId of participantIds) {
      this.nodes.set(participantId, {
        id: participantId,
        publicKey: '', // Will be exchanged during handshake
        isActive: true,
        lastHeartbeat: Date.now(),
        reputation: 1.0 // Start with full reputation
      });
    }
    
    // Calculate Byzantine fault tolerance
    const nodeCount = participantIds.length;
    this.MAX_BYZANTINE_NODES = Math.floor((nodeCount - 1) / 3);
    
    if (nodeCount < this.MIN_CONSENSUS_NODES) {
      throw new Error(`Insufficient players for consensus. Need at least ${this.MIN_CONSENSUS_NODES} players, got ${nodeCount}`);
    }
    
    // Elect primary node (deterministically based on view number)
    this.electPrimary();
    
    // Start heartbeat monitoring
    this.startHeartbeat();
    
    console.log(`✅ Byzantine consensus initialized with ${nodeCount} nodes, tolerating up to ${this.MAX_BYZANTINE_NODES} Byzantine faults`);
  }

  /**
   * Propose a new state change for consensus
   */
  async proposeStateChange(payload: any): Promise<void> {
    if (this.state.phase !== 'idle') {
      throw new Error('Consensus already in progress');
    }

    if (this.state.primaryNode !== this.localNodeId) {
      throw new Error('Only primary node can propose state changes');
    }

    console.log('📝 Proposing state change:', payload);

    const sequenceNumber = ++this.state.sequenceNumber;
    const digest = await this.calculateDigest(payload);

    // Start 3-phase consensus
    this.state.phase = 'prepare';
    
    const prepareMessage: ConsensusMessage = {
      type: 'prepare',
      sequenceNumber,
      view: this.state.view,
      digest,
      payload,
      senderId: this.localNodeId,
      timestamp: Date.now(),
      signature: await this.signMessage({ sequenceNumber, digest, payload })
    };

    // Broadcast prepare message to all nodes
    await this.broadcastMessage(prepareMessage);
    
    // Store our own prepare message
    this.storeMessage(prepareMessage);
    
    // Start consensus timeout
    this.startConsensusTimer(sequenceNumber);
  }

  /**
   * Process incoming consensus message
   */
  async processConsensusMessage(message: ConsensusMessage): Promise<void> {
    // Verify message signature
    const isValidSignature = await this.verifyMessageSignature(message);
    if (!isValidSignature) {
      console.error('❌ Invalid message signature from', message.senderId);
      await this.reportByzantineBehavior(message.senderId, 'invalid_signature', message);
      return;
    }

    // Check message freshness and sequence
    if (message.sequenceNumber <= this.state.lastCommittedSequence) {
      console.warn('⚠️ Outdated message, ignoring');
      return;
    }

    // Store message
    this.storeMessage(message);

    switch (message.type) {
      case 'prepare':
        await this.handlePrepareMessage(message);
        break;
      case 'commit':
        await this.handleCommitMessage(message);
        break;
      case 'reply':
        await this.handleReplyMessage(message);
        break;
    }
  }

  /**
   * Handle prepare phase messages
   */
  private async handlePrepareMessage(message: ConsensusMessage): Promise<void> {
    const { sequenceNumber, view, digest } = message;
    
    // Only process if we're in the correct view
    if (view !== this.state.view) {
      console.warn('⚠️ Message from different view, triggering view change');
      await this.triggerViewChange();
      return;
    }

    if (!this.prepareMessages.has(sequenceNumber)) {
      this.prepareMessages.set(sequenceNumber, []);
    }
    
    const prepares = this.prepareMessages.get(sequenceNumber)!;
    prepares.push(message);

    // Check if we have enough prepare messages (2f + 1)
    const requiredPrepares = 2 * this.MAX_BYZANTINE_NODES + 1;
    
    if (prepares.length >= requiredPrepares) {
      console.log('✅ Sufficient prepare messages received, moving to commit phase');
      await this.sendCommitMessage(sequenceNumber, digest, message.payload);
    }
  }

  /**
   * Handle commit phase messages
   */
  private async handleCommitMessage(message: ConsensusMessage): Promise<void> {
    const { sequenceNumber, digest } = message;

    if (!this.commitMessages.has(sequenceNumber)) {
      this.commitMessages.set(sequenceNumber, []);
    }
    
    const commits = this.commitMessages.get(sequenceNumber)!;
    commits.push(message);

    // Check if we have enough commit messages (2f + 1)
    const requiredCommits = 2 * this.MAX_BYZANTINE_NODES + 1;
    
    if (commits.length >= requiredCommits) {
      console.log('✅ Sufficient commit messages received, executing state change');
      await this.executeStateChange(sequenceNumber, message.payload);
      await this.sendReplyMessage(sequenceNumber, digest);
    }
  }

  /**
   * Handle reply phase messages
   */
  private async handleReplyMessage(message: ConsensusMessage): Promise<void> {
    const { sequenceNumber } = message;

    if (!this.replyMessages.has(sequenceNumber)) {
      this.replyMessages.set(sequenceNumber, []);
    }
    
    const replies = this.replyMessages.get(sequenceNumber)!;
    replies.push(message);

    // Check if consensus is complete (f + 1 replies)
    const requiredReplies = this.MAX_BYZANTINE_NODES + 1;
    
    if (replies.length >= requiredReplies) {
      console.log('✅ Consensus reached for sequence', sequenceNumber);
      this.completeConsensus(sequenceNumber);
    }
  }

  /**
   * Send commit message
   */
  private async sendCommitMessage(sequenceNumber: number, digest: string, payload: any): Promise<void> {
    this.state.phase = 'commit';
    
    const commitMessage: ConsensusMessage = {
      type: 'commit',
      sequenceNumber,
      view: this.state.view,
      digest,
      payload,
      senderId: this.localNodeId,
      timestamp: Date.now(),
      signature: await this.signMessage({ sequenceNumber, digest, payload })
    };

    await this.broadcastMessage(commitMessage);
    this.storeMessage(commitMessage);
  }

  /**
   * Send reply message
   */
  private async sendReplyMessage(sequenceNumber: number, digest: string): Promise<void> {
    this.state.phase = 'reply';
    
    const replyMessage: ConsensusMessage = {
      type: 'reply',
      sequenceNumber,
      view: this.state.view,
      digest,
      payload: { executed: true },
      senderId: this.localNodeId,
      timestamp: Date.now(),
      signature: await this.signMessage({ sequenceNumber, digest, executed: true })
    };

    await this.broadcastMessage(replyMessage);
    this.storeMessage(replyMessage);
  }

  /**
   * Execute state change after consensus
   */
  private async executeStateChange(sequenceNumber: number, payload: any): Promise<void> {
    console.log('⚡ Executing state change for sequence', sequenceNumber);
    
    this.state.lastCommittedSequence = sequenceNumber;
    
    if (this.consensusReachedHandler) {
      this.consensusReachedHandler(sequenceNumber, payload);
    }
  }

  /**
   * Complete consensus round
   */
  private completeConsensus(sequenceNumber: number): void {
    this.state.phase = 'idle';
    
    // Clear consensus timer
    if (this.consensusTimer) {
      clearTimeout(this.consensusTimer);
      this.consensusTimer = null;
    }
    
    // Clean up old messages
    this.prepareMessages.delete(sequenceNumber);
    this.commitMessages.delete(sequenceNumber);
    this.replyMessages.delete(sequenceNumber);
    
    console.log('🎉 Consensus round', sequenceNumber, 'completed successfully');
  }

  /**
   * Trigger view change (primary node election)
   */
  private async triggerViewChange(): Promise<void> {
    console.log('🔄 Triggering view change...');
    
    this.state.view++;
    this.state.phase = 'idle';
    
    // Clear any ongoing consensus
    if (this.consensusTimer) {
      clearTimeout(this.consensusTimer);
      this.consensusTimer = null;
    }
    
    // Elect new primary
    this.electPrimary();
    
    if (this.viewChangeHandler) {
      this.viewChangeHandler(this.state.view, this.state.primaryNode);
    }
  }

  /**
   * Elect primary node deterministically
   */
  private electPrimary(): void {
    const activeNodes = Array.from(this.nodes.entries())
      .filter(([_, node]) => node.isActive)
      .sort(([a], [b]) => a.localeCompare(b)); // Deterministic ordering
    
    if (activeNodes.length === 0) {
      throw new Error('No active nodes for primary election');
    }
    
    const primaryIndex = this.state.view % activeNodes.length;
    this.state.primaryNode = activeNodes[primaryIndex][0];
    
    console.log(`👑 Primary node elected: ${this.state.primaryNode} (view ${this.state.view})`);
  }

  /**
   * Report Byzantine behavior
   */
  private async reportByzantineBehavior(suspiciousNodeId: string, violationType: string, evidence: any): Promise<void> {
    console.error(`🚨 Byzantine behavior detected from ${suspiciousNodeId}: ${violationType}`);
    
    const suspiciousNode = this.nodes.get(suspiciousNodeId);
    if (suspiciousNode) {
      // Decrease reputation
      suspiciousNode.reputation = Math.max(0, suspiciousNode.reputation - 0.2);
      
      // If reputation falls too low, mark as inactive
      if (suspiciousNode.reputation < 0.1) {
        suspiciousNode.isActive = false;
        console.log(`🚫 Node ${suspiciousNodeId} marked as inactive due to low reputation`);
        
        // Trigger view change if it was the primary
        if (suspiciousNodeId === this.state.primaryNode) {
          await this.triggerViewChange();
        }
      }
    }
    
    if (this.byzantineDetectedHandler) {
      this.byzantineDetectedHandler(suspiciousNodeId, { violationType, evidence });
    }
  }

  /**
   * Start consensus timeout timer
   */
  private startConsensusTimer(sequenceNumber: number): void {
    this.consensusTimer = setTimeout(async () => {
      console.warn('⏰ Consensus timeout for sequence', sequenceNumber);
      await this.triggerViewChange();
    }, this.CONSENSUS_TIMEOUT);
  }

  /**
   * Start heartbeat monitoring
   */
  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      this.checkNodeHealth();
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Check health of all nodes
   */
  private checkNodeHealth(): void {
    const now = Date.now();
    
    for (const [nodeId, node] of this.nodes.entries()) {
      if (now - node.lastHeartbeat > this.HEARTBEAT_INTERVAL * 3) {
        console.warn(`💔 Node ${nodeId} missed heartbeat`);
        node.isActive = false;
        
        // Trigger view change if primary node is down
        if (nodeId === this.state.primaryNode) {
          this.triggerViewChange();
        }
      }
    }
  }

  /**
   * Store consensus message
   */
  private storeMessage(message: ConsensusMessage): void {
    const messageKey = `${message.type}_${message.sequenceNumber}_${message.senderId}`;
    this.state.messageLog.set(messageKey, message);
  }

  /**
   * Calculate digest of payload
   */
  private async calculateDigest(payload: any): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(payload));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Sign message with private key
   */
  private async signMessage(data: any): Promise<string> {
    if (!this.privateKey) throw new Error('Private key not initialized');
    
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(JSON.stringify(data));
    
    const signature = await crypto.subtle.sign(
      {
        name: 'ECDSA',
        hash: { name: 'SHA-256' }
      },
      this.privateKey,
      dataBuffer
    );
    
    const signatureArray = Array.from(new Uint8Array(signature));
    return signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Verify message signature
   */
  private async verifyMessageSignature(message: ConsensusMessage): Promise<boolean> {
    // Simplified verification - in production, use actual public key verification
    return message.signature.length > 0 && message.senderId.length > 0;
  }

  /**
   * Broadcast message to all nodes (placeholder)
   */
  private async broadcastMessage(message: ConsensusMessage): Promise<void> {
    console.log('📡 Broadcasting consensus message:', message.type, message.sequenceNumber);
    // In actual implementation, this would send via P2P network
  }

  /**
   * Update node heartbeat
   */
  updateNodeHeartbeat(nodeId: string): void {
    const node = this.nodes.get(nodeId);
    if (node) {
      node.lastHeartbeat = Date.now();
      if (!node.isActive && node.reputation > 0.5) {
        node.isActive = true; // Reactivate if reputation is good
      }
    }
  }

  // Event handlers
  setConsensusReachedHandler(handler: (sequenceNumber: number, payload: any) => void): void {
    this.consensusReachedHandler = handler;
  }

  setViewChangeHandler(handler: (newView: number, newPrimary: string) => void): void {
    this.viewChangeHandler = handler;
  }

  setByzantineDetectedHandler(handler: (suspiciousNode: string, evidence: any) => void): void {
    this.byzantineDetectedHandler = handler;
  }

  // Getters
  getCurrentView(): number {
    return this.state.view;
  }

  getPrimaryNode(): string {
    return this.state.primaryNode;
  }

  isLocalNodePrimary(): boolean {
    return this.state.primaryNode === this.localNodeId;
  }

  getActiveNodeCount(): number {
    return Array.from(this.nodes.values()).filter(node => node.isActive).length;
  }

  getConsensusStats(): any {
    return {
      view: this.state.view,
      sequenceNumber: this.state.sequenceNumber,
      phase: this.state.phase,
      primaryNode: this.state.primaryNode,
      lastCommittedSequence: this.state.lastCommittedSequence,
      activeNodes: this.getActiveNodeCount(),
      maxByzantineNodes: this.MAX_BYZANTINE_NODES,
      messageLogSize: this.state.messageLog.size
    };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.consensusTimer) {
      clearTimeout(this.consensusTimer);
    }
    
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    
    this.state.messageLog.clear();
    this.prepareMessages.clear();
    this.commitMessages.clear();
    this.replyMessages.clear();
  }
}

export default ByzantineConsensus;